import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SuccessBanner } from "./components/SuccessBanner";
import { CancelledBanner } from "./components/CancelledBanner";
import { ProductCard } from "./components/ProductCard";
import { CreditsSection } from "./components/CreditsSection";
import { RCBillingBanner } from "./components/RCBillingBanner";
import { isCurrentUserAdmin } from "@utils/adminUtils";
import { requestDeleteAccount } from "@remotes/requestDeleteAccount";
import { useAuth } from "@contexts/AuthContext";
import { useLoading } from "@hooks/useLoading";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { css } from "@emotion/css";

import { useSubscriptionLogic } from "./hooks/useSubscriptionLogic";
import {
  containerStyle,
  contentStyle,
  sectionStyle,
  sectionTitleStyle,
  detailRowStyle,
  detailLabelStyle,
  detailValueStyle,
  transactionCardStyle,
  productsGridStyle,
  loadingStyle,
  errorStyle,
  sectionDividerStyle,
  subsectionTitleStyle,
  planCardStyle,
  planHeaderStyle,
  planNameStyle,
  bubbleTagStyle,
  statusStyle,
  bottomRowStyle,
  managementTextStyle,
  chooseButtonStyle,
  manageButtonStyle,
  adminButtonStyle,
  buttonContainerStyle,
  appleBannerStyle,
  appleBannerContentStyle,
  appleBannerIconStyle,
  appleBannerTextStyle,
  appleBannerTitleStyle,
  appleBannerMessageStyle,
} from "./styles";

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [deactivateLoading, startDeactivateLoading, stopDeactivateLoading] = useLoading();
  const [error, handleError] = useErrorHandling();
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  const {
    status,
    subscription,
    subscriptionLoading,
    productsLoading,
    checkoutLoading,
    availableProducts,
    isTrial,
    isAppleSubscription,
    formatDate,
    getTierColor,
    getRenewableTypeLabel,
    handleUpgradeClick,
    handleChooseSubscription,
    handleManageSubscriptionUrl,
    isRCBillingSubscription,
  } = useSubscriptionLogic();

  const handleAdminClick = () => {
    navigate('/admin');
  };

  const handleDeactivateAccount = async () => {
    if (!showDeactivateConfirm) {
      setShowDeactivateConfirm(true);
      return;
    }

    startDeactivateLoading();
    try {
      await requestDeleteAccount();
      logout();
      navigate("/login");
    } catch (e) {
      handleError(e);
    } finally {
      stopDeactivateLoading();
      setShowDeactivateConfirm(false);
    }
  };

  if (subscriptionLoading || productsLoading) {
    return (
      <div className={loadingStyle}>Loading subscription details...</div>
    );
  }

  if (!subscription) {
    return (
      <div className={errorStyle}>No subscription found</div>
    );
  }

  return (
    <div className={containerStyle}>
      {/* Success Banner */}
      {status === 'success' && (
        <SuccessBanner
          title="Payment Successful!"
          message="Your subscription has been activated. You can now use your new credits."
        />
      )}

      {/* Cancelled Banner */}
      {status === 'cancelled' && (
        <CancelledBanner
          title="Purchase Cancelled"
          message="Your purchase was cancelled. You can try again anytime."
        />
      )}

      {/* Apple Subscription Management Banner */}
      {isAppleSubscription() && (
        <div className={appleBannerStyle}>
          <div className={appleBannerContentStyle}>
            <span className={appleBannerIconStyle}>📱</span>
            <div className={appleBannerTextStyle}>
              <h3 className={appleBannerTitleStyle}>Manage Your Subscription in the Wand iOS App</h3>
              <p className={appleBannerMessageStyle}>
                Your subscription is managed through the Apple App Store. To make changes, upgrade, or cancel your subscription, please use the Wand iOS app or visit your Apple ID settings.
              </p>
            </div>
          </div>
        </div>
      )}

      {isRCBillingSubscription() && (
        <RCBillingBanner />
      )}

      <div className={contentStyle}>
        {/* Overview Section - Combined Plan & Credits */}
        <div className={sectionStyle}>
          <div className={planCardStyle}>
            <div className={planHeaderStyle}>
              <span className={planNameStyle}>
                {isTrial() ? "Trial Plan" : subscription.product.display_name}
              </span>
              <span className={`${bubbleTagStyle} ${statusStyle}`}>
                {isTrial() ? "TRIAL" : "ACTIVE"}
              </span>
            </div>
            
            {/* Credits Display */}
            <CreditsSection subscription={subscription} isTrial={isTrial} />
            
            <div className={bottomRowStyle}>
              <div className={managementTextStyle}>
                {isTrial()
                  ? (subscription.end_date
                      ? `Your trial will end on ${formatDate(subscription.end_date)}. Upgrade to continue using Wand.`
                      : `Started ${formatDate(subscription.start_date)}. Upgrade to continue using Wand.`)
                  : (subscription.end_date
                      ? `Your ${subscription.product.display_name} plan will renew on ${formatDate(subscription.end_date)}.`
                      : `Started ${formatDate(subscription.start_date)}.`)
                }
              </div>
              <div className={buttonContainerStyle}>
                {!isAppleSubscription() && (
                  <button className={chooseButtonStyle} onClick={handleChooseSubscription}>
                    {isRCBillingSubscription() ? "Change Subscription Plan" : "Choose Subscription Plan"}
                  </button>
                )}
                {subscription.management_url && (
                  <button className={manageButtonStyle} onClick={handleManageSubscriptionUrl}>
                    Manage Subscription
                  </button>
                )}
                {isCurrentUserAdmin() && (
                  <button className={adminButtonStyle} onClick={handleAdminClick}>
                    Admin Dashboard
                  </button>
                )}
                <button 
                  className={deactivateButtonStyle} 
                  onClick={handleDeactivateAccount}
                  disabled={deactivateLoading}
                >
                  {showDeactivateConfirm 
                    ? (deactivateLoading ? "Deactivating..." : "Confirm Deactivation") 
                    : "Deactivate Account"
                  }
                </button>
              </div>
            </div>
          </div>

          {/* Show deactivation confirmation message */}
          {showDeactivateConfirm && (
            <div className={confirmationMessageStyle}>
              <p>⚠️ Are you sure you want to deactivate your account? You can reactivate it later by logging in again.</p>
              <button 
                className={cancelDeactivateButtonStyle} 
                onClick={() => setShowDeactivateConfirm(false)}
                disabled={deactivateLoading}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Show error message if any */}
          {error && (
            <div className={errorMessageStyle}>
              {error}
            </div>
          )}

          {/* Available Plans for Trial/NonRenewable */}
          {availableProducts.length > 0 && (
            <>
              <div className={sectionDividerStyle} />
              <div data-section="available-plans">
                <h3 className={subsectionTitleStyle}>Available Plans</h3>
                <div className={productsGridStyle}>
                  {availableProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      getTierColor={getTierColor}
                      getRenewableTypeLabel={getRenewableTypeLabel}
                      onUpgradeClick={handleUpgradeClick}
                      isLoading={checkoutLoading === product.id}
                      isAppleSubscription={isAppleSubscription()}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Transaction Details Section - Only show if needed */}
        {process.env.NODE_ENV !== "production" && subscription.transaction_id && (
          <div className={sectionStyle}>
            <h2 className={sectionTitleStyle}>Transaction Details</h2>
            <div className={transactionCardStyle}>
              <div className={detailRowStyle}>
                <span className={detailLabelStyle}>Transaction ID:</span>
                <span className={detailValueStyle}>{subscription.transaction_id}</span>
              </div>
              {subscription.original_transaction_id && (
                <div className={detailRowStyle}>
                  <span className={detailLabelStyle}>Original Transaction ID:</span>
                  <span className={detailValueStyle}>{subscription.original_transaction_id}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Styles
const deactivateButtonStyle = css`
  background: #dc3545;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  align-self: flex-start;

  &:hover:not(:disabled) {
    background: #c82333;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const confirmationMessageStyle = css`
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  text-align: center;

  p {
    color: #ffffff;
    margin: 0 0 16px 0;
    font-size: 16px;
  }
`;

const cancelDeactivateButtonStyle = css`
  background: #6c757d;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: #5a6268;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const errorMessageStyle = css`
  background: rgba(240, 66, 81, 0.1);
  border: 1px solid rgba(240, 66, 81, 0.3);
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
  color: #f04251;
  text-align: center;
  font-size: 14px;
`;

export default SubscriptionPage; 